import React, { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import type { RouteComponentProps } from 'react-router';

import { Flows } from '../internals/flows';
import { DatahubActions } from '../redux-store/thunks/datahub.thunks';
import { CustomConnectionsThunk } from '../redux-store/thunks/customConnectionURL.thunks';
import { SubActions } from '../redux-store/thunks/subscriptions.thunks';
import { commandPaletteToggled } from '../redux-store/thunks/global.thunks';
import { ctrlSpaceKeyboardsHandlerFn } from 'Internals/global-shortcuts';
import { findSubscription } from '../utils/subscription.utils';

import useDelayedEffect from '../hooks/useDelayedEffect';
import { useTryConnectViaURL } from '../hooks/useTryConnectViaURL';
import { useCloseSocketIOConnection, useSocketIO } from '../hooks/useSocketIO';

import {
  DatahubObjectsPalette,
  DatahubObjectsPaletteId,
} from '../containers/DatahubObjectsPalette.container';
import { MainLayoutContainer } from '../containers/MainLayout.container';

import { ConnectMessages } from '../components/connect-subscription/ConnectMessages.view';

import type { RootState } from '../types/state.types';

const CONNECTION_FAILED_TIMEOUT = 5000; // ms

export const SubscriptionContext = createContext({ subId: '' });

const SubscriptionPage: React.FC<PropsWithChildren<RouteComponentProps<{}, any>>> = (props) => {
  const { children, params, router, routes, location } = props;
  const { subId } = params;

  const dispatch = useDispatch();
  const subscriptionId = useSelector((state: RootState) => state.subscription.id);

  const { closeConnection } = useSocketIO(subId);

  useCloseSocketIOConnection(subscriptionId, closeConnection);

  useTryConnectViaURL(subId, location?.query?.connection_url);

  const showDatahubObjectsPalette = useSelector(
    (state: RootState) => state.globals.commandPaletteShown === DatahubObjectsPaletteId
  );

  const subIdFromProps = useSelector((state: RootState) => state.subscription.id);
  const sub = useSelector((state: RootState) =>
    findSubscription(state.subscriptions)(state.subscription.id)
  );

  const closeCommandPalette = () => dispatch(commandPaletteToggled(undefined));
  const toggleDatahubObjectsPalette = () =>
    dispatch(commandPaletteToggled(DatahubObjectsPaletteId));

  useEffect(() => {
    dispatch(CustomConnectionsThunk.setAllCustomConnections());
  }, [CustomConnectionsThunk.setAllCustomConnections, dispatch]);

  useEffect(() => {
    if (sub === undefined && subIdFromProps !== '') {
      // Current user has left, or deleted the subscription.
      router.push('/dashboard');
    }
  }, [router, sub, subIdFromProps]);

  useEffect(() => {
    const toggleObjectsPalette = (ev: KeyboardEvent) => {
      ev.preventDefault();

      toggleDatahubObjectsPalette();
    };

    const ctrlSpaceKeyboardsHandler = ctrlSpaceKeyboardsHandlerFn(toggleObjectsPalette);

    window.addEventListener('keydown', ctrlSpaceKeyboardsHandler);

    return () => window.removeEventListener('keydown', ctrlSpaceKeyboardsHandler);
  }, []);

  useEffect(() => {
    function disconnect() {
      dispatch(SubActions.disconnect(true));
      dispatch(DatahubActions.clearInfo());
      Flows.clear();
    }

    router.setRouteLeaveHook(routes[1], disconnect);
  }, []);

  const [showConnectionFailPanel, setShowConnectionFailPanel] = useState(false);

  useDelayedEffect(
    () => {}, // noop as we don't need to do anything here
    () => setShowConnectionFailPanel(true),
    CONNECTION_FAILED_TIMEOUT,
    sub
  );

  let content = null;

  if (showConnectionFailPanel) {
    content = <ConnectMessages connectStatus="connection_failed" />;
  } else {
    content = children;
  }

  return (
    <SubscriptionContext.Provider value={subId}>
      <MainLayoutContainer displayNavBar={true} location={window.location}>
        {content}
        {showDatahubObjectsPalette && (
          // @ts-ignore
          <DatahubObjectsPalette onRequestClose={closeCommandPalette} />
        )}
      </MainLayoutContainer>
    </SubscriptionContext.Provider>
  );
};

// @ts-ignore
const Wrapped = withRouter(SubscriptionPage);

export { Wrapped as SubscriptionPage };
